import React, { createRef, useState, useRef, useEffect } from 'react';
import qs from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';

import { Language, Utils, isValidVerificationLink } from '../../utils';
import { user as userActions } from '../../actions';
import { Page, Button, Confirm, OnboardingWarningModal } from '../../components';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';

import makeClasses from './styles';

import appLogo from '../../static/images/logo.svg';
import HelpIcon from '../../static/images/icons/help.svg';
import clsx from 'clsx';
import { Checkbox, Input, InputLabel, useMediaQuery, useTheme } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import PhoneAndroidOutlinedIcon from '@material-ui/icons/PhoneAndroidOutlined';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import EmailOutlinedIcon from '@material-ui/icons/EmailOutlined';
import moment from 'moment';
import { replace } from 'connected-react-router';
import FlagOutlinedIcon from '@material-ui/icons/FlagOutlined';
import SentimentDissatisfiedOutlinedIcon from '@material-ui/icons/SentimentDissatisfiedOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { OB_TYPES } from '../../utils/Api';
import Markdown from 'markdown-to-jsx';

const TIMER_TIME = 60 * 5;

const RegisterScreen = ({ history }) => {
  const dispatch = useDispatch();
  const classes = makeClasses();
  const searchParams = qs.parse(history.location.search);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { loading, languageTexts } = useSelector(state => ({
    loading: state.user.loading,
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [registerStep, setRegisterStep] = useState(searchParams.step ? parseInt(searchParams.step) : 1);
  const [step2Completed, setStep2Completed] = useState(false);
  const [step3Completed, setStep3Completed] = useState(false);
  const [attemptLimitReachedError, setAttemptLimitReachedError] = useState(false);
  const [affiliationStep1TraceId, setAffiliationStep1TraceId] = useState(
    searchParams.affiliationStep1TraceId || null
  );
  const [affiliationStep2TraceId, setAffiliationStep2TraceId] = useState(
    searchParams.affiliationStep2TraceId || null
  );
  const [affiliationStep3TraceId, setAffiliationStep3TraceId] = useState(
    searchParams.affiliationStep3TraceId || null
  );
  const initialLink = searchParams.verificationLink;

  const [affiliationVerificationLink, setAffiliationVerificationLink] = useState(() => {
    if (initialLink && isValidVerificationLink(initialLink)) {
      return decodeURIComponent(initialLink);
    }
    return null;
  });

  // Step 1 Data
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cities, setCities] = useState([]);

  // Step 1 Inputs
  const [inputCi, setInputCi] = useState('');
  const [inputActivity, setInputActivity] = useState('');
  const [inputCategory, setInputCategory] = useState(null);
  const [inputCountry, setInputCountry] = useState(null);
  const [inputDepartment, setInputDepartment] = useState(null);
  const [inputCity, setInputCity] = useState(null);
  const [inputAddressStreet, setInputAddressStreet] = useState('');
  const [inputAddressNumber, setInputAddressNumber] = useState('');
  const [inputAddressApartment, setInputAddressApartment] = useState('');
  const [inputAddressCorner, setInputAddressCorner] = useState('');
  const [inputEmail, setInputEmail] = useState('');
  const [inputPhone, setInputPhone] = useState('');
  const [inputPhoneCharacteristic, setInputPhoneCharacteristic] = useState(null);
  //
  const verificationCodeRefs = useRef(new Array(6).fill('').map(() => createRef()));
  const [verificationCode, setVerificationCode] = useState(new Array(6).fill(''));
  const [timerStartTime, setTimerStartTime] = useState(null);
  const [intervalTime, setIntervalTime] = useState(0);
  const [intervalId, setIntervalId] = useState(null);
  const [formattedTime, setFormattedTime] = useState('00:00');
  const [showHelp, setShowHelp] = useState(false);
  const [acceptedHandyTermsAndConditions, setAcceptedHandyTermsAndConditions] = useState(false);
  const [acceptedPaigoTermsAndConditions, setAcceptedPaigoTermsAndConditions] = useState(false);
  const [hasCiError, setHasCiError] = useState(false)
  const [hasCiAPIError, setHasCiAPIError] = useState(false)
  const [hasEmailError, setHasEmailError] = useState(false)
  const [hasEmailAPIError, setHasEmailAPIError] = useState(false)
  const [hasPhoneError, setHasPhoneError] = useState(false)
  const [hasPhoneAPIError, setHasPhoneAPIError] = useState(false)

  const canMoveToStep2 =
    inputCi.trim().length > 0 &&
    inputActivity.trim().length > 0 &&
    inputCategory !== null &&
    inputCountry !== null &&
    inputDepartment !== null &&
    inputCity !== null &&
    inputAddressStreet.length > 0 &&
    inputAddressNumber.length > 0 &&
    inputEmail.length > 0 &&
    inputPhone.length > 0 &&
    inputPhone.length <= 15 &&
    inputPhoneCharacteristic !== null &&
    inputAddressCorner.length > 0 &&
    acceptedHandyTermsAndConditions &&
    acceptedPaigoTermsAndConditions &&
    !hasCiError &&
    !hasEmailError &&
    !hasPhoneError;
  const canValidateVerificationCode = verificationCode.filter(code => /[0-9]/.test(code)).length;

  useEffect(() => {
    dispatch(
      userActions.getCountries(countries => {
        setCountries(countries);
        setInputPhoneCharacteristic(countries);
        countries.forEach(country => {
          if (country.alphaCode2 === 'UY') {
            setInputCountry(country.id);
            setInputPhoneCharacteristic(country.id);
          }
        });
      })
    );
  }, []);

  useEffect(() => {
    dispatch(
      userActions.getCategories(OB_TYPES.PHYSICAL, categories => {
        setCategories(categories);
      })
    );
  }, []);

  useEffect(
    () => {
      if (searchParams.result) {
        if (searchParams.result === 'success' || searchParams.result === 'error') {
          setRegisterStep(5);
        } else if (searchParams.result === 'expired') {
          setRegisterStep(6);
        } else {
          setShowWarningModal(true);
        }
      } else {
        setShowWarningModal(true);
      }
    },
    [searchParams.result]
  );

  useEffect(
    () => {
      if (!intervalId) {
        const id = setInterval(() => {
          setIntervalTime(currentIntervalTime => currentIntervalTime + 1);
        }, 1000);
        setIntervalId(id);
      }
    },
    [intervalId]
  );

  useEffect(
    () => {
      if (timerStartTime) {
        const timer = TIMER_TIME - moment().diff(moment(timerStartTime), 's');
        if (timer < 0) {
          _stopTimer();
        } else {
          const minutes = Math.floor(timer / 60);
          const seconds = timer % 60;
          setFormattedTime(`${minutes < 10 ? '0' + minutes : minutes}:${seconds < 10 ? '0' + seconds : seconds}`);
        }
      }
    },
    [intervalTime, timerStartTime]
  );

  useEffect(
    () => {
      if (inputCountry) {
        dispatch(
          userActions.getDepartmentsByCountry(inputCountry, departments => {
            setDepartments(departments);
          })
        );
      }
    },
    [inputCountry]
  );

  useEffect(
    () => {
      if (inputDepartment) {
        dispatch(
          userActions.getCitiesByDepartments(inputDepartment, cities => {
            setCities(cities);
          })
        );
      }
    },
    [inputDepartment]
  );

  useEffect(
    () => {
      const searchParams = qs.parse(history.location.search) || {};
      searchParams.step = registerStep;
      searchParams.affiliationStep1TraceId = affiliationStep1TraceId || undefined;

      if (affiliationStep1TraceId) {
        _stopTimer();
        setVerificationCode(new Array(6).fill(''));

        if (registerStep === 2) {
          verificationCodeRefs.current[0].current.focus();

          dispatch(
            userActions.affiliationRequestEmailVerificationCode(affiliationStep1TraceId, step2TraceId => {
              _startTimer();

              if (step2TraceId) {
                setAffiliationStep2TraceId(step2TraceId);

                if (!searchParams.affiliationStep2TraceId || searchParams.affiliationStep2TraceId !== step2TraceId) {
                  searchParams.affiliationStep2TraceId = step2TraceId;
                  dispatch(
                    replace({
                      search: qs.stringify(searchParams),
                    })
                  );
                }
              }
            })
          );
        } else if (registerStep === 3) {
          verificationCodeRefs.current[0].current.focus();

          dispatch(
            userActions.affiliationRequestPhoneVerificationCode(affiliationStep1TraceId, step3TraceId => {
              _startTimer();

              if (step3TraceId) {
                setAffiliationStep3TraceId(step3TraceId);

                if (!searchParams.affiliationStep3TraceId || searchParams.affiliationStep3TraceId !== step3TraceId) {
                  searchParams.affiliationStep3TraceId = step3TraceId;
                  dispatch(
                    replace({
                      search: qs.stringify(searchParams),
                    })
                  );
                }
              }
            })
          );
        } else if (registerStep === 4) {
          dispatch(
            userActions.affiliationGetIdentityVerificationLink(affiliationStep1TraceId, result => {
              const safeLink = result.verificationLink;
              if (isValidVerificationLink(safeLink)) {
                setAffiliationVerificationLink(safeLink);
                searchParams.verificationLink = safeLink;
                dispatch(replace({ search: qs.stringify(searchParams) }));
              } else {
                toast.error(i18n.get('AlertInvalidVerificationLink'), { autoClose: 5000 });
              }
            })
          );
        } else {
          dispatch(
            replace({
              search: qs.stringify(searchParams),
            })
          );
        }
      }
    },
    [registerStep]
  );

  useEffect(
    () => {
      if (affiliationVerificationLink) {
        const requestCameraPermission = async () => {
          await navigator.mediaDevices.getUserMedia({
            video: true,
          });
        };
        requestCameraPermission();
      }
    },
    [affiliationVerificationLink]
  );

  useEffect(() => _validateVerificationCode(), [canValidateVerificationCode]);

  const _handleOnChangeCategory = (e, newValue) => {
    setInputCategory(newValue);
  };

  const _handleOnChangeCountry = (e, newValue) => {
    setInputDepartment(null);
    setInputCity(null);
    setDepartments([]);
    setCities([]);
    setInputCountry(newValue);
  };

  const _handleOnChangeDepartment = (e, newValue) => {
    setInputCity(null);
    setCities([]);
    setInputDepartment(newValue);
  };

  const _handleOnChangePhoneCharacteristic = (e, newValue) => {
    setInputPhoneCharacteristic(newValue);
  };

  const _handleOnChangeCity = (e, newValue) => {
    setInputCity(newValue);
  };

  const _submitRegisterStep1 = () => {

    const country = countries.find(c => c.id === inputCountry);

    const city = cities.find(c => c.id === inputCity);

    const category = categories.find(c => c.id === inputCategory);

    const countryPhone = countries.find(c => c.id === inputPhoneCharacteristic);

    const phone = inputPhone.trim();

    const completePhoneNumber = countries.find(c => c.id === inputPhoneCharacteristic).dialingCode + phone;

    const ciError = !Utils.inputs.ci.regex.test(inputCi.trim())
    if (ciError) {
      setHasCiError(true)
    }
    const emailError = !Utils.inputs.email.regex.test(inputEmail.trim()) 
    if (emailError) {
      setHasEmailError(true)  
    }
    const phoneError = !Utils.validatePhoneNumber(completePhoneNumber)
    if (phoneError) {
      setHasPhoneError(true)
    }

    if (phoneError || ciError || emailError) {
      return
    }

    dispatch(
      userActions.startAffiliation(
        {
          CountryISO: country.alphaCode2,
          DocumentTypeKey: 'CI',
          Document: inputCi.trim(),
          CategoryId: category.id,
          Phone: {
            CountryId: countryPhone.id,
            Number: phone,
          },
          Email: inputEmail.trim(),
          Address: {
            CityId: city.id,
            Street: inputAddressStreet.trim(),
            Corner: inputAddressCorner.trim(),
            Apartment: inputAddressApartment.trim(),
            PostalCode: null,
            Number: inputAddressNumber.trim(),
            Description: null,
            Longitude: 0,
            Latitude: 0,
          },
          ActivityName: inputActivity.trim(),
          AffiliationCode: qs.parse(history?.location?.search)?.code ?? null
        },
        (affiliationResult, error) => {
          if (error) {
            handleErrorResponse(affiliationResult);
            return
          }
          if (affiliationResult.traceId) {
            setAffiliationStep1TraceId(affiliationResult.traceId);
            setRegisterStep(2);
          }
        }
      )
    );
  };

  const handleErrorResponse = (e) => {
    let messageCode = 'Error'
    if (
      e.message.Message === 'There is already an affiliation with the email' ||
      e.message.Message === 'There is a pending affiliation with the email'
    ) {
      messageCode = 'Register.RegisterErrorEmailAlreadyInUse';
      setHasEmailAPIError(true)
    } else if (
      e.message.Message === 'There is already an affiliation with the phone' ||
      e.message.Message === 'There is a pending affiliation with the phone'
    ) {
      messageCode = 'Register.RegisterErrorPhoneAlreadyInUse';
      setHasPhoneAPIError(true)
    } else if (e.message.Message === 'There is already a pending affiliation' || e.message.Message === 'There is already an affiliation with the document') {
      messageCode = 'Register.RegisterErrorCiAlreadyInUse';
      setHasCiAPIError(true)
    } else if (e.message.Message === 'Limit of affiliation attempts has been reached') {
      messageCode = 'Register.RegisterLimitOfAffiliationAttemptsReached';
    } else if (e.message.Message === 'Document has invalid format.') {
      messageCode = 'Register.RegisterInvalidCi';
      setHasCiAPIError(true)
    }
    toast.error(<div className={classes.toastContent}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.0001 22.0251C11.7334 22.0251 11.4793 21.9751 11.2376 21.8751C10.9959 21.7751 10.7751 21.6334 10.5751 21.4501L2.5501 13.4251C2.36676 13.2251 2.2251 13.0043 2.1251 12.7626C2.0251 12.5209 1.9751 12.2668 1.9751 12.0001C1.9751 11.7334 2.0251 11.4751 2.1251 11.2251C2.2251 10.9751 2.36676 10.7584 2.5501 10.5751L10.5751 2.5501C10.7751 2.3501 10.9959 2.20426 11.2376 2.1126C11.4793 2.02093 11.7334 1.9751 12.0001 1.9751C12.2668 1.9751 12.5251 2.02093 12.7751 2.1126C13.0251 2.20426 13.2418 2.3501 13.4251 2.5501L21.4501 10.5751C21.6501 10.7584 21.7959 10.9751 21.8876 11.2251C21.9793 11.4751 22.0251 11.7334 22.0251 12.0001C22.0251 12.2668 21.9793 12.5209 21.8876 12.7626C21.7959 13.0043 21.6501 13.2251 21.4501 13.4251L13.4251 21.4501C13.2418 21.6334 13.0251 21.7751 12.7751 21.8751C12.5251 21.9751 12.2668 22.0251 12.0001 22.0251ZM12.0001 20.0251L20.0251 12.0001L12.0001 3.9751L3.9751 12.0001L12.0001 20.0251ZM12.0001 13.0001C12.2834 13.0001 12.5209 12.9043 12.7126 12.7126C12.9043 12.5209 13.0001 12.2834 13.0001 12.0001V8.0001C13.0001 7.71676 12.9043 7.47926 12.7126 7.2876C12.5209 7.09593 12.2834 7.0001 12.0001 7.0001C11.7168 7.0001 11.4793 7.09593 11.2876 7.2876C11.0959 7.47926 11.0001 7.71676 11.0001 8.0001V12.0001C11.0001 12.2834 11.0959 12.5209 11.2876 12.7126C11.4793 12.9043 11.7168 13.0001 12.0001 13.0001ZM12.0001 16.0001C12.2834 16.0001 12.5209 15.9043 12.7126 15.7126C12.9043 15.5209 13.0001 15.2834 13.0001 15.0001C13.0001 14.7168 12.9043 14.4793 12.7126 14.2876C12.5209 14.0959 12.2834 14.0001 12.0001 14.0001C11.7168 14.0001 11.4793 14.0959 11.2876 14.2876C11.0959 14.4793 11.0001 14.7168 11.0001 15.0001C11.0001 15.2834 11.0959 15.5209 11.2876 15.7126C11.4793 15.9043 11.7168 16.0001 12.0001 16.0001Z" fill="white"/>
      </svg>
    <span>{i18n.get(messageCode)}</span>
  </div>, { autoClose: 5000 });
  }

  const _submitRegisterStep2 = () => {
    setRegisterStep(3);
  };

  const _submitRegisterStep3 = () => {
    setRegisterStep(4);
  };

  const _startTimer = () => {
    setTimerStartTime(Date.now());
  };

  const _stopTimer = () => {
    setTimerStartTime(null);
    if (intervalId) {
      clearInterval(intervalId);
    }
    setIntervalId(null);
  };

  const _validateVerificationCode = () => {
    const hasValidVerificationCode = verificationCode.filter(code => /[0-9]/.test(code)).length === 6;

    if (canValidateVerificationCode && hasValidVerificationCode) {
      dispatch(
        userActions.affiliationVerifyVerificationCode(
          affiliationStep1TraceId,
          registerStep === 2 ? affiliationStep2TraceId : affiliationStep3TraceId,
          verificationCode.join(''),
          result => {
            if (result.result === 'SUCCESSFUL') {
              _stopTimer();
              if (registerStep === 2) {
                setStep2Completed(true);
              } else if (registerStep === 3) {
                setStep3Completed(true);
              }
            } else if (result.result === 'WRONG_ACCESS_CODE') {
              toast.error(i18n.get('Register.RegisterCodeValidationError'), { autoClose: 5000 });
            } else if (result.result === 'ATTEMPT_LIMIT_REACHED') {
              setAttemptLimitReachedError(true);
            } else if (result.result === 'EXPIRED_ACCESS_CODE') {
              _stopTimer();
              toast.error(i18n.get('Register.RegisterExpiredValidationCodeError'), { autoClose: 5000 });
            }
          }
        )
      );
    }
  };

  const _handleVerificationCodeOnBlur = index => e => {
    if (e.target.value.length > 0 && !/[0-9]/.test(e.target.value)) {
      const newVerificationCode = verificationCode.slice();
      newVerificationCode[index] = '';
      setVerificationCode(newVerificationCode);
      verificationCodeRefs.current[index].current.focus();
    }
  };

  const _handleVerificationCodeOnChange = index => e => {
    if (e.target.value.length === 0) {
      const newVerificationCode = verificationCode.slice();
      newVerificationCode[index] = '';
      return setVerificationCode(newVerificationCode);
    }

    if (!/[0-9]/.test(e.target.value)) {
      return;
    }

    const newVerificationCode = verificationCode.slice();
    newVerificationCode[index] = e.target.value.substring(0, 1);
    setVerificationCode(newVerificationCode);

    if (index + 1 < 6) {
      verificationCodeRefs.current[index + 1].current.focus();
    } else if (index === 5) {
      setVerificationCode(newVerificationCode);
    }
  };

  const _handleVerificationCondeOnPaste = e => {
    const pastedText = e.clipboardData.getData('text').trim();

    if (!/[0-9]{6}/.test(pastedText)) {
      return alert(i18n.get('Register.CodeInvalidCode'));
    }

    const newVerificationCode = verificationCode.slice();
    for (let i = 0, l = 6; i < l; i += 1) {
      newVerificationCode[i] = pastedText[i];
    }

    setVerificationCode(newVerificationCode);
  };

  const _requestNewVerificationCode = () => {
    setVerificationCode(new Array(6).fill(''));

    if (registerStep === 2) {
      dispatch(
        userActions.affiliationRequestEmailVerificationCode(affiliationStep1TraceId, step2TraceId => {
          if (step2TraceId === false) {
            setAttemptLimitReachedError(true);
          } else if (typeof step2TraceId === 'string') {
            setAffiliationStep2TraceId(step2TraceId);
            _startTimer();
          }
        })
      );
    } else if (registerStep === 3) {
      dispatch(
        userActions.affiliationRequestPhoneVerificationCode(affiliationStep1TraceId, step3TraceId => {
          if (step3TraceId === false) {
            setAttemptLimitReachedError(true);
          } else if (typeof step3TraceId === 'string') {
            setAffiliationStep3TraceId(step3TraceId);
            _startTimer();
          }
        })
      );
    }
  };

  const _handleOnChangeInputPhone = e => {
    setHasPhoneError(false)
    setHasPhoneAPIError(false)
    const value = e.target.value
    if (value.length <= 15 && /^\d*$/.test(value)) {
      setInputPhone(value);
    }
    const phone = value.trim();
    const completePhoneNumber = countries.find(c => c.id === inputPhoneCharacteristic).dialingCode + phone;
    const phoneError = !Utils.validatePhoneNumber(completePhoneNumber)
    if (phoneError) {
      setHasPhoneError(true)
    }
    
  };

  const handleCiChange = (e) => {
    const value = e.target.value
    setInputCi(value)
    setHasCiError(false)
    setHasCiAPIError(false)
    const ciError = !Utils.inputs.ci.regex.test(value.trim())
    if (ciError) {
      setHasCiError(true)
    }
  }

  const handleEmailChange = (e) => {
    const value = e.target.value
    setInputEmail(value)
    setHasEmailError(false)
    setHasEmailAPIError(false)
    const emailError = !Utils.inputs.email.regex.test(value.trim()) 
    if (emailError) {
      setHasEmailError(true)  
    }
  }

  const _goBack = () => {
    if (registerStep === 2) {
      setRegisterStep(1);
    } else if (registerStep === 3) {
      setRegisterStep(2);
    } else {
      setRegisterStep(1);
      setInputCi('');
      setInputCategory(-1);
      setInputCountry(-1);
      setInputDepartment(-1);
      setInputCity(-1);
      setInputPhoneCharacteristic(-1);
      setInputAddressStreet('');
      setInputAddressNumber('');
      setInputAddressApartment('');
      setInputAddressCorner('');
      setInputEmail('');
      setInputPhone('');

      Object.keys(searchParams).map(key => delete searchParams[key]);

      dispatch(
        replace({
          search: qs.stringify(searchParams),
        })
      );
    }
  };

  const _toggleShowHelp = () => {
    setShowHelp(!showHelp);
  };

  const _toggleShowWarningModal = () => {
    setShowWarningModal(!showWarningModal);
  };

  const _getOptionLabel = array => option => {
    let foundOption = null;
    if (array === 'categories') {
      foundOption = categories.find(c => c.id === option);
    }
    else if (array === 'countries') {
      foundOption = countries.find(c => c.id === option);
    } else if (array === 'cities') {
      foundOption = cities.find(c => c.id === option);
    } else if (array === 'departments') {
      foundOption = departments.find(c => c.id === option);
    }
    else if (array === 'phoneCharacteristics') {
      foundOption = countries.find(c => c.id === option);
      if (foundOption)
        return foundOption.name + " " + foundOption.dialingCode;
    }
    if (foundOption) {
      return foundOption.name;
    }
    return '';
  };

  const renderRegisterForm = () => {
    if (registerStep === 1) {
      return renderStep1();
    }
    if (registerStep === 2) {
      return renderStep2();
    }
    if (registerStep === 3) {
      return renderStep3();
    }
    if (registerStep === 4) {
      return renderStep4();
    }
    if (registerStep === 5) {
      return renderStep5();
    }
    if (registerStep === 6) {
      return renderStep6();
    }
  };

  const renderStep1 = () => {
    return (
      <React.Fragment>
        <div className={classes.formContent}>
          <div className={classes.formTitleContainer}>
            <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
            <div className={classes.formText}>{i18n.get('Register.Step1FormText')}</div>
          </div>
          <form layout="vertical" onSubmit={_submitRegisterStep1} className={classes.form}>
            <Grid container spacing={2} >
              <Grid item xs={12} sm={12} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputActivityTitle')}
                </InputLabel>
                <TextField
                  placeholder={i18n.get('Register.InputActivityTitle')}
                  className={classes.formInput}
                  onChange={e => setInputActivity(e.target.value)}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputActivity}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputCategoryLabel')}
                </InputLabel>
                <Autocomplete
                  options={categories.map(c => c.id)}
                  getOptionLabel={_getOptionLabel('categories')}
                  onChange={_handleOnChangeCategory}
                  classes={{ inputRoot: classes.formInputSelect }}
                  value={inputCategory}
                  renderInput={params => (
                    <TextField
                      className={classes.formInputTrimmed}
                      classes={{ root: classes.formInput }}
                      variant="outlined"
                      placeholder={i18n.get('Register.InputCategoryTitle')}
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid className={classes.inputWithErrorContainer} item xs={6} sm={6} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {isSmallScreen ? i18n.get('Register.InputCIMobileLabel') : i18n.get('Register.InputCILabel')}
                </InputLabel>
                <TextField
                  error={hasCiError || hasCiAPIError}
                  placeholder={i18n.get('Register.InputCITitle')}
                  className={classes.formInput}
                  onChange={handleCiChange}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputCi}
                  InputProps={{
                    classes: { input: classes.formInputTrimmed }
                  }}
                />
                {hasCiError && 
                  <div className={classes.errorMessageContainer}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.00001 16.5189C8.80001 16.5189 8.60939 16.4814 8.42814 16.4064C8.24689 16.3314 8.08126 16.2252 7.93126 16.0877L1.91251 10.0689C1.77501 9.91895 1.66876 9.75332 1.59376 9.57207C1.51876 9.39082 1.48126 9.2002 1.48126 9.0002C1.48126 8.8002 1.51876 8.60645 1.59376 8.41895C1.66876 8.23145 1.77501 8.06895 1.91251 7.93145L7.93126 1.9127C8.08126 1.7627 8.24689 1.65332 8.42814 1.58457C8.60939 1.51582 8.80001 1.48145 9.00001 1.48145C9.20001 1.48145 9.39376 1.51582 9.58126 1.58457C9.76876 1.65332 9.93126 1.7627 10.0688 1.9127L16.0875 7.93145C16.2375 8.06895 16.3469 8.23145 16.4156 8.41895C16.4844 8.60645 16.5188 8.8002 16.5188 9.0002C16.5188 9.2002 16.4844 9.39082 16.4156 9.57207C16.3469 9.75332 16.2375 9.91895 16.0875 10.0689L10.0688 16.0877C9.93126 16.2252 9.76876 16.3314 9.58126 16.4064C9.39376 16.4814 9.20001 16.5189 9.00001 16.5189ZM9.00001 9.7502C9.21251 9.7502 9.39064 9.67832 9.53439 9.53457C9.67814 9.39082 9.75001 9.2127 9.75001 9.0002V6.0002C9.75001 5.7877 9.67814 5.60957 9.53439 5.46582C9.39064 5.32207 9.21251 5.2502 9.00001 5.2502C8.78751 5.2502 8.60939 5.32207 8.46564 5.46582C8.32189 5.60957 8.25001 5.7877 8.25001 6.0002V9.0002C8.25001 9.2127 8.32189 9.39082 8.46564 9.53457C8.60939 9.67832 8.78751 9.7502 9.00001 9.7502ZM9.00001 12.0002C9.21251 12.0002 9.39064 11.9283 9.53439 11.7846C9.67814 11.6408 9.75001 11.4627 9.75001 11.2502C9.75001 11.0377 9.67814 10.8596 9.53439 10.7158C9.39064 10.5721 9.21251 10.5002 9.00001 10.5002C8.78751 10.5002 8.60939 10.5721 8.46564 10.7158C8.32189 10.8596 8.25001 11.0377 8.25001 11.2502C8.25001 11.4627 8.32189 11.6408 8.46564 11.7846C8.60939 11.9283 8.78751 12.0002 9.00001 12.0002Z" fill="#FF6467"/>
                    </svg>
                    <div className={classes.errorMessage}>{i18n.get('Register.CiErrorMessage')}</div>
                  </div>
                }
              </Grid>
              <Grid item xs={6} sm={6} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputCountryTitle')}
                </InputLabel>
                <Autocomplete
                  options={countries.map(c => c.id)}
                  getOptionLabel={_getOptionLabel('countries')}
                  onChange={_handleOnChangeCountry}
                  classes={{ inputRoot: classes.formInputSelect }}
                  disabled
                  value={inputCountry}
                  renderInput={params => (
                    <TextField
                      className={classes.formInput}
                      classes={{ root: classes.formInput }}
                      variant="outlined"
                      placeholder={i18n.get('Register.InputCountryTitle')}
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputDepartmentLabel')}
                </InputLabel>
                <Autocomplete
                  options={departments.map(d => d.id)}
                  getOptionLabel={_getOptionLabel('departments')}
                  onChange={_handleOnChangeDepartment}
                  classes={{ inputRoot: classes.formInputSelect }}
                  disabled={!inputCountry}
                  value={inputDepartment}
                  renderInput={params => (
                    <TextField
                      className={classes.formInput}
                      classes={{ root: classes.formInput }}
                      variant="outlined"
                      placeholder={i18n.get('Register.InputDepartmentTitle')}
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputCityLabel')}
                </InputLabel>
                <Autocomplete
                  options={cities.map(c => c.id)}
                  getOptionLabel={_getOptionLabel('cities')}
                  onChange={_handleOnChangeCity}
                  classes={{ inputRoot: classes.formInputSelect }}
                  disabled={!inputDepartment}
                  value={inputCity}
                  renderInput={params => (
                    <TextField
                      className={classes.formInput}
                      classes={{ root: classes.formInput }}
                      variant="outlined"
                      placeholder={i18n.get('Register.InputCityTitle')}
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} xl={12}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputStreetLabel')}
                </InputLabel>
                <TextField
                  placeholder={i18n.get('Register.InputStreetTitle')}
                  className={classes.formInput}
                  onChange={e => setInputAddressStreet(e.target.value)}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputAddressStreet}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} xl={4}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputNumberLabel')}
                </InputLabel>
                <TextField
                  placeholder={i18n.get('Register.InputNumberTitle')}
                  className={classes.formInput}
                  onChange={e => setInputAddressNumber(e.target.value)}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputAddressNumber}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={4} xl={4}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputApartmentTitle')}
                </InputLabel>
                <TextField
                  placeholder={i18n.get('Register.InputApartmentTitle')}
                  className={classes.formInput}
                  onChange={e => setInputAddressApartment(e.target.value)}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputAddressApartment}
                  InputProps={{
                    classes: { input: classes.formInputTrimmed }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={4} xl={4}>
                 <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputCornerLabel')}
                </InputLabel>
                <TextField
                  placeholder={i18n.get('Register.InputCornerTitle')}
                  className={classes.formInput}
                  onChange={e => setInputAddressCorner(e.target.value)}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputAddressCorner}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} xl={12}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputEmailLabel')}
                </InputLabel>
                <TextField
                  error={hasEmailError || hasEmailAPIError}
                  placeholder={i18n.get('Register.InputEmailTitle')}
                  className={classes.formInput}
                  onChange={handleEmailChange}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputEmail}
                />
                {hasEmailError && 
                  <div className={classes.errorMessageContainer}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.00001 16.5189C8.80001 16.5189 8.60939 16.4814 8.42814 16.4064C8.24689 16.3314 8.08126 16.2252 7.93126 16.0877L1.91251 10.0689C1.77501 9.91895 1.66876 9.75332 1.59376 9.57207C1.51876 9.39082 1.48126 9.2002 1.48126 9.0002C1.48126 8.8002 1.51876 8.60645 1.59376 8.41895C1.66876 8.23145 1.77501 8.06895 1.91251 7.93145L7.93126 1.9127C8.08126 1.7627 8.24689 1.65332 8.42814 1.58457C8.60939 1.51582 8.80001 1.48145 9.00001 1.48145C9.20001 1.48145 9.39376 1.51582 9.58126 1.58457C9.76876 1.65332 9.93126 1.7627 10.0688 1.9127L16.0875 7.93145C16.2375 8.06895 16.3469 8.23145 16.4156 8.41895C16.4844 8.60645 16.5188 8.8002 16.5188 9.0002C16.5188 9.2002 16.4844 9.39082 16.4156 9.57207C16.3469 9.75332 16.2375 9.91895 16.0875 10.0689L10.0688 16.0877C9.93126 16.2252 9.76876 16.3314 9.58126 16.4064C9.39376 16.4814 9.20001 16.5189 9.00001 16.5189ZM9.00001 9.7502C9.21251 9.7502 9.39064 9.67832 9.53439 9.53457C9.67814 9.39082 9.75001 9.2127 9.75001 9.0002V6.0002C9.75001 5.7877 9.67814 5.60957 9.53439 5.46582C9.39064 5.32207 9.21251 5.2502 9.00001 5.2502C8.78751 5.2502 8.60939 5.32207 8.46564 5.46582C8.32189 5.60957 8.25001 5.7877 8.25001 6.0002V9.0002C8.25001 9.2127 8.32189 9.39082 8.46564 9.53457C8.60939 9.67832 8.78751 9.7502 9.00001 9.7502ZM9.00001 12.0002C9.21251 12.0002 9.39064 11.9283 9.53439 11.7846C9.67814 11.6408 9.75001 11.4627 9.75001 11.2502C9.75001 11.0377 9.67814 10.8596 9.53439 10.7158C9.39064 10.5721 9.21251 10.5002 9.00001 10.5002C8.78751 10.5002 8.60939 10.5721 8.46564 10.7158C8.32189 10.8596 8.25001 11.0377 8.25001 11.2502C8.25001 11.4627 8.32189 11.6408 8.46564 11.7846C8.60939 11.9283 8.78751 12.0002 9.00001 12.0002Z" fill="#FF6467"/>
                    </svg>
                    <div className={classes.errorMessage}>{i18n.get('Register.EmailErrorMEssage')}</div>
                  </div>
                }
              </Grid>
              <Grid item xs={6} sm={6} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputPhoneCharacteristicLabel')}
                </InputLabel>
                <Autocomplete
                  options={countries.map(d => d.id)}
                  getOptionLabel={_getOptionLabel('phoneCharacteristics')}
                  onChange={_handleOnChangePhoneCharacteristic}
                  classes={{ inputRoot: classes.formInputSelect }}
                  value={inputPhoneCharacteristic}
                  renderInput={params => (
                    <TextField
                      className={classes.formInput}
                      classes={{ root: classes.formInput }}
                      variant="outlined"
                      placeholder={i18n.get('Register.InputPhoneCharacteristic')}
                      {...params}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6} sm={6} md={6} xl={6}>
                <InputLabel className={classes.formSelectLabel} >
                  {i18n.get('Register.InputPhoneLabel')}
                </InputLabel>
                <TextField
                  error={hasPhoneError || hasPhoneAPIError}
                  placeholder={i18n.get('Register.InputPhoneTitle')}
                  className={classes.formInput}
                  onChange={_handleOnChangeInputPhone}
                  classes={{ root: classes.formInput }}
                  variant="outlined"
                  value={inputPhone}
                  InputProps={{
                    classes: { input: classes.formInputTrimmed }
                  }}
                />
                {hasPhoneError && 
                  <div className={classes.errorMessageContainer}>
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.00001 16.5189C8.80001 16.5189 8.60939 16.4814 8.42814 16.4064C8.24689 16.3314 8.08126 16.2252 7.93126 16.0877L1.91251 10.0689C1.77501 9.91895 1.66876 9.75332 1.59376 9.57207C1.51876 9.39082 1.48126 9.2002 1.48126 9.0002C1.48126 8.8002 1.51876 8.60645 1.59376 8.41895C1.66876 8.23145 1.77501 8.06895 1.91251 7.93145L7.93126 1.9127C8.08126 1.7627 8.24689 1.65332 8.42814 1.58457C8.60939 1.51582 8.80001 1.48145 9.00001 1.48145C9.20001 1.48145 9.39376 1.51582 9.58126 1.58457C9.76876 1.65332 9.93126 1.7627 10.0688 1.9127L16.0875 7.93145C16.2375 8.06895 16.3469 8.23145 16.4156 8.41895C16.4844 8.60645 16.5188 8.8002 16.5188 9.0002C16.5188 9.2002 16.4844 9.39082 16.4156 9.57207C16.3469 9.75332 16.2375 9.91895 16.0875 10.0689L10.0688 16.0877C9.93126 16.2252 9.76876 16.3314 9.58126 16.4064C9.39376 16.4814 9.20001 16.5189 9.00001 16.5189ZM9.00001 9.7502C9.21251 9.7502 9.39064 9.67832 9.53439 9.53457C9.67814 9.39082 9.75001 9.2127 9.75001 9.0002V6.0002C9.75001 5.7877 9.67814 5.60957 9.53439 5.46582C9.39064 5.32207 9.21251 5.2502 9.00001 5.2502C8.78751 5.2502 8.60939 5.32207 8.46564 5.46582C8.32189 5.60957 8.25001 5.7877 8.25001 6.0002V9.0002C8.25001 9.2127 8.32189 9.39082 8.46564 9.53457C8.60939 9.67832 8.78751 9.7502 9.00001 9.7502ZM9.00001 12.0002C9.21251 12.0002 9.39064 11.9283 9.53439 11.7846C9.67814 11.6408 9.75001 11.4627 9.75001 11.2502C9.75001 11.0377 9.67814 10.8596 9.53439 10.7158C9.39064 10.5721 9.21251 10.5002 9.00001 10.5002C8.78751 10.5002 8.60939 10.5721 8.46564 10.7158C8.32189 10.8596 8.25001 11.0377 8.25001 11.2502C8.25001 11.4627 8.32189 11.6408 8.46564 11.7846C8.60939 11.9283 8.78751 12.0002 9.00001 12.0002Z" fill="#FF6467"/>
                    </svg>
                    <div className={classes.errorMessage}>{i18n.get('Register.PhoneErrorMessage')}</div>
                  </div>
                }
              </Grid>
            </Grid>
          </form>
        </div>
        <div className={classes.termsWrapper}>
          <div className={classes.termsContainer}>
            <Checkbox
              classes={{ root: classes.checkboxRoot }}
              checked={acceptedHandyTermsAndConditions}
              onChange={() => setAcceptedHandyTermsAndConditions(!acceptedHandyTermsAndConditions)}
            />
            <Markdown className={classes.termsLabel}>
              {i18n.get('Register.HandyTyC')}
            </Markdown>
          </div>
          <div className={classes.termsContainer}>
            <Checkbox
              classes={{ root: classes.checkboxRoot }}
              checked={acceptedPaigoTermsAndConditions}
              onChange={() => setAcceptedPaigoTermsAndConditions(!acceptedPaigoTermsAndConditions)}
            />
            <Markdown className={classes.termsLabel}>
              {i18n.get('Register.PaigoTyCLabel')}
            </Markdown>
          </div>
        </div>
        <div className={clsx(classes.formSubmitContainer, classes.formSubmitContainerWithOneButton)}>
          <Button
            className={classes.formSubmitButton}
            onClick={_submitRegisterStep1}
            disabled={loading || !canMoveToStep2}
          >
            {i18n.get('Register.NextButton')}
          </Button>
        </div>
      </React.Fragment>
    );
  };

  const renderStep2 = () => {
    return (
      <React.Fragment>
        <div className={classes.formContent}>
          <div className={clsx(classes.formTitleContainer, classes.formTitleContainerWithAlert)}>
            <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
            {!attemptLimitReachedError ? (
              <div className={classes.formText}>{i18n.get('Register.Step2FormText')}</div>
            ) : null}
          </div>
          {attemptLimitReachedError ? (
            <React.Fragment>
              <Alert severity="error" icon={false} className={clsx(classes.alert, classes.alertError)}>
                <div>
                  <CancelOutlinedIcon className={classes.alertIcon} />
                </div>
                <div className={classes.alertMessage}>
                  <AlertTitle className={classes.alertTitle}>
                    {i18n.get('Register.AlertAttemptLimitReachedErrorTitle')}
                  </AlertTitle>
                  {i18n.get('Register.AlertAttemptLimitReachedErrorMessage')}
                </div>
              </Alert>
            </React.Fragment>
          ) : step2Completed ? (
            <React.Fragment>
              <Alert severity="success" icon={false} className={clsx(classes.alert, classes.alertSuccess)}>
                <div>
                  <CheckCircleOutlineOutlinedIcon className={classes.alertIcon} />
                </div>
                <div className={classes.alertMessage}>
                  <AlertTitle className={classes.alertTitle}>{i18n.get('Register.AlertSuccessStep2Title')}</AlertTitle>
                  {i18n.get('Register.AlertSuccessStep2Message')}
                </div>
              </Alert>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Alert severity="warning" icon={false} className={clsx(classes.alert, classes.alertWarning)}>
                <div>
                  <EmailOutlinedIcon className={classes.emailIcon} />
                </div>
                <div className={classes.alertMessage}>
                  <AlertTitle className={classes.alertTitle}>{i18n.get('Register.AlertWarningStep2Title').replace('{email}', Utils.maskEmail(inputEmail))}</AlertTitle>
                  {i18n.get('Register.AlertWarningStep2Message1')}
                  <span className={classes.alertTimer}>{formattedTime}</span>
                  <span>{i18n.get('Register.AlertWarningStep2Message2')}</span>
                </div>
              </Alert>
              <form layout="vertical" className={clsx(classes.form, classes.formVerificationCode)}>
                <Grid container spacing={2} className={classes.verificationCodeGridContainer}>
                  {verificationCode.map((code, index) => (
                    <Grid key={index} item xs={2} sm={2} md={2} className={classes.codeInputContainer}>
                      <Input
                        value={code}
                        onBlur={_handleVerificationCodeOnBlur(index)}
                        onChange={_handleVerificationCodeOnChange(index)}
                        inputProps={{ className: classes.verificationCodeInput, inputMode: 'numeric' }}
                        inputRef={verificationCodeRefs.current[index]}
                        onPaste={_handleVerificationCondeOnPaste}
                        disableUnderline={true}
                      />
                    </Grid>
                  ))}
                 {!step2Completed && <Grid xs={12}>
                    <Button
                      variant="outlined"
                      className={clsx(classes.resendCodeButton, timerStartTime ? classes.resendCodeButtonDisabled : null)}
                      onClick={_requestNewVerificationCode}
                      disabled={timerStartTime}
                    >
                      {i18n.get('Register.RequestNewVerificationCodeButton')}
                    </Button>
                  </Grid>}
                </Grid>
              </form>
            </React.Fragment>
          )}
        </div>
        {!attemptLimitReachedError ? (
          <div className={classes.formSubmitContainer}>
            <Button className={classes.formSubmitButton} onClick={_goBack} variant="secondary" disabled>
              {i18n.get('Register.ReturnButton')}
            </Button>
            <div className={classes.formSubmitButtonSeparator} />
            <Button
              className={classes.formSubmitButton}
              onClick={_submitRegisterStep2}
              disabled={loading || !step2Completed}
            >
              {i18n.get('Register.NextButton')}
            </Button>
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  const renderStep3 = () => {
    return (
      <React.Fragment>
        <div className={classes.formContent}>
          <div className={clsx(classes.formTitleContainer, classes.formTitleContainerWithAlert)}>
            <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
            {!attemptLimitReachedError ? (
              <div className={classes.formText}>{i18n.get('Register.Step3FormText')}</div>
            ) : null}
          </div>
          {attemptLimitReachedError ? (
            <React.Fragment>
              <Alert severity="error" icon={false} className={clsx(classes.alert, classes.alertError)}>
                <div className={classes.alertMessage}>
                  <AlertTitle className={classes.alertTitle}>
                    {i18n.get('Register.AlertAttemptLimitReachedErrorTitle')}
                  </AlertTitle>
                  {i18n.get('Register.AlertAttemptLimitReachedErrorMessage')}
                </div>
              </Alert>
            </React.Fragment>
          ) : step3Completed ? (
            <React.Fragment>
              <Alert severity="success" icon={false} className={clsx(classes.alert, classes.alertSuccess)}>
                <div>
                  <CheckCircleOutlineOutlinedIcon className={classes.alertIcon} />
                </div>
                <div className={classes.alertMessage}>
                  <AlertTitle className={classes.alertTitle}>{i18n.get('Register.AlertSuccessStep3Title')}</AlertTitle>
                  {i18n.get('Register.AlertSuccessStep3Message')}
                </div>
              </Alert>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Alert severity="warning" icon={false} className={clsx(classes.alert, classes.alertWarning)}>
                <div>
                  <PhoneAndroidOutlinedIcon className={classes.alertIcon} />
                </div>
                <div className={classes.alertMessage}>
                  <AlertTitle className={classes.alertTitle}>{i18n.get('Register.AlertWarningStep3Title').replace('{phone}', Utils.maskPhoneNumber(inputPhone))}</AlertTitle>
                  {i18n.get('Register.AlertWarningStep3Message1')}
                  <span className={classes.alertTimer}>{formattedTime}</span>
                  <span>{i18n.get('Register.AlertWarningStep3Message2')}</span>
                </div>
              </Alert>
              <form layout="vertical" className={clsx(classes.form, classes.formVerificationCode)}>
                <Grid container spacing={2} className={classes.verificationCodeGridContainer}>
                  {verificationCode.map((code, index) => (
                    <Grid key={index} item xs={2} sm={2} md={2} className={classes.codeInputContainer}>
                      <Input
                        value={code}
                        onBlur={_handleVerificationCodeOnBlur(index)}
                        onChange={_handleVerificationCodeOnChange(index)}
                        inputProps={{ className: classes.verificationCodeInput, inputMode: 'numeric' }}
                        inputRef={verificationCodeRefs.current[index]}
                        onPaste={_handleVerificationCondeOnPaste}
                        disableUnderline={true}
                      />
                    </Grid>
                  ))}
                 {!step3Completed && <Grid xs={12}>
                    <Button
                      variant="outlined"
                      className={clsx(classes.resendCodeButton, timerStartTime ? classes.resendCodeButtonDisabled : null)}
                      onClick={_requestNewVerificationCode}
                      disabled={timerStartTime}
                    >
                      {i18n.get('Register.RequestNewVerificationCodeButton')}
                    </Button>
                  </Grid>}
                </Grid>
              </form>
            </React.Fragment>
          )}
        </div>
        {!attemptLimitReachedError ? (
          <div className={classes.formSubmitContainer}>
            <Button className={classes.formSubmitButton} onClick={_goBack} variant="secondary" disabled>
              {i18n.get('Register.ReturnButton')}
            </Button>
            <div className={classes.formSubmitButtonSeparator} />
            <Button
              className={classes.formSubmitButton}
              onClick={_submitRegisterStep3}
              disabled={loading || !step3Completed}
            >
              {i18n.get('Register.NextButton')}
            </Button>
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  const renderStep4 = () => {
    return (
      <React.Fragment>
        <div className={classes.formContent}>
          <div className={classes.formTitleContainer}>
            <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
            {!affiliationVerificationLink ? (
              <div className={classes.formText}>{i18n.get('Register.Step4FormText')}</div>
            ) : null}
          </div>
        </div>
        {affiliationVerificationLink ? (
          <div className={classes.iframeContainer}>
            <iframe src={affiliationVerificationLink} title="Verification" allow="camera *;" />
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  const renderStep5 = () => {
    return (
      <React.Fragment>
        <div className={classes.formContent}>
          <div className={classes.textContainer}>
            <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
            <div className={classes.formText}>{i18n.get('Register.Step5FormText')}</div>
          </div>
          <Alert severity="warning" icon={false} className={clsx(classes.alert, classes.alertWarning, classes.alertWithNoMarginBottom)}>
            <FlagOutlinedIcon className={clsx(classes.alertIcon, classes.alertIconWithBorder)} />
            <div className={classes.alertMessage}>
              <AlertTitle className={classes.alertTitle}>{i18n.get('Register.Step5ConfirmationTitle')}</AlertTitle>
              <div>{i18n.get('Register.Step5ConfirmationMessage1')}</div>
            </div>
          </Alert>
          <div className={classes.textContainer}>
            <div className={classes.primaryText}>{i18n.get('Register.DownloadAppTitle')}</div>
            <div className={classes.secondaryText}>{i18n.get('Register.DownloadAppText')}</div>
            <div className={classes.appDownloadContainer}>
              <a target="_blank" rel="noreferrer" href="https://apps.apple.com/us/app/handy-uy/id6504303787">
                <img src="/download-app-store.png" alt="App Store Badge" />
              </a>
              <a target="_blank" rel="noreferrer" href="https://play.google.com/store/apps/details?id=uy.handy.app_handy_main_app">
                <img src="/download-play-store.png" alt="Google Play Store Badge" />
              </a>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  };

  const renderStep6 = () => {
    return (
      <React.Fragment>
        <div className={classes.formContent}>
          <div className={classes.formTitleContainer}>
            <div className={classes.formTitle}>{i18n.get('Register.StepFormTitle')}</div>
          </div>
          <Alert severity="warning" icon={false} className={clsx(classes.alert, classes.alertError, classes.alertWithNoMarginBottom)}>
            <SentimentDissatisfiedOutlinedIcon className={classes.alertIcon} />
            <div className={classes.alertMessage}>
              <AlertTitle className={classes.alertTitle}>{i18n.get('Register.Step6ConfirmationTitle')}</AlertTitle>
              <div>{i18n.get('Register.Step6ConfirmationMessage')}</div>
            </div>
          </Alert>
          <div className={clsx(classes.formSubmitContainer, classes.formSubmitContainerWithOneButton)}>
            <Button className={classes.formSubmitButton} onClick={_goBack}>
              {i18n.get('Register.BackToStart')}
            </Button>
          </div>
        </div>
      </React.Fragment>
    );
  };

  return (
    <Page
      withHeader={false}
      withSideMenu={false}
      withBanner={false}
      withBoxBanner={false}
      showBranchWithNoPaymentMethodWarning={false}
      renderCustomToast={<ToastContainer
        className={classes.toast}
        position="top-center"
        autoClose={5000}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        hideProgressBar
        pauseOnFocusLoss={false}
        draggable={false}
        theme="light"
/>}
    >
      <div className={classes.container}>
        <div
          className={clsx(
            classes.contentContainer,
            registerStep === 4 && affiliationVerificationLink ? classes.contentContainerWithIFrame : null
          )}
        >
          <div
            className={clsx(
              classes.contentWrapper,
              affiliationVerificationLink ? classes.contentWrapperWithIFrame : null
            )}
          >
            <div className={classes.brandContainer}>
              <img src={appLogo} className={classes.brandLogo} alt="Handy Logo" />
              <div className={classes.brandSeparator} />
              <div className={classes.brandTitle}>{i18n.get('Register.PageTitle')}</div>
            </div>
          </div>
          {renderRegisterForm()}
        </div>
        <div className={classes.helpContainer} onClick={_toggleShowHelp}>
          {i18n.get('Register.UserHelpTitle')}
          <img src={HelpIcon} className={classes.helpIcon} alt="ayuda" />
        </div>
      </div>
      <Confirm
        open={showHelp}
        onClose={_toggleShowHelp}
        title={i18n.get('Register.UserHelpDialogTitle')}
        text={i18n.get('Register.UserHelpDialogText')}
        confirmText={i18n.get('Register.UserHelpDialogButton')}
        onConfirm={_toggleShowHelp}
      />
      <OnboardingWarningModal open={showWarningModal} onClose={_toggleShowWarningModal} />
    </Page>
  );
};

RegisterScreen.id = 'com.Handy.Register';

export default RegisterScreen;
